import { Box, Stack, Typography } from "@mui/material";
import {
  useUnassignedMerchantsQuery,
  type UnassignedMerchantsParams,
} from "Api/Queries/Merchants/useUnassignedMerchantsQuery";
import { AssignHunterDialog } from "Components/Admin/UnassignedMerchants/AssignHunterDialog";
import { UnassignedMerchantsTable } from "Components/Admin/UnassignedMerchants/UnassignedMerchantsTable";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useServerTable } from "Hooks/useServerTable";
import type { UnassignedMerchantListItemDto } from "Infrastructure/Api/Api";
import { useMemo, useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { asStartOfDayISO, asString, asStringArray } from "Utils/FilterUtils";

const RESOURCES = Resources.Hunter.UnassignedMerchants;

// Keep in sync with UnassignedMerchantsFilterRequest.AllowedSortColumns on BE.
const SORT_COLUMN_MAP: Record<string, string> = {
  merchantStatus: "Status",
  "party.companyName": "Party.CompanyName",
  "party.companyNumber": "Party.CompanyNumber",
  dateCreated: "DateCreated",
};

export const UnassignedMerchantsPageContent = () => {
  const [selectedMerchant, setSelectedMerchant] =
    useState<UnassignedMerchantListItemDto | null>(null);
  const { t } = useResource();

  const { queryParams, tableProps } =
    useServerTable<UnassignedMerchantListItemDto>({
      defaultSorting: [{ id: "dateCreated", desc: true }],
    });

  const apiParams = useMemo((): UnassignedMerchantsParams => {
    const dateCreatedFrom = asStartOfDayISO(queryParams.filters.dateCreated);

    return {
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy
        ? SORT_COLUMN_MAP[queryParams.sortBy]
        : undefined,
      sortDirection: queryParams.sortDirection,
      statuses: asStringArray(queryParams.filters.merchantStatus),
      companyName: asString(queryParams.filters["party.companyName"]),
      companyNumber: asString(queryParams.filters["party.companyNumber"]),
      dateCreatedFrom,
    };
  }, [queryParams]);

  const { data, isLoading, isRefetching } =
    useUnassignedMerchantsQuery(apiParams);

  if (!isLoading && !data) {
    return <ErrorText>{t(RESOURCES.LoadingError)}</ErrorText>;
  }

  const merchants = data?.items ?? [];
  const hasActiveFilters = Object.keys(queryParams.filters).length > 0;
  const isEmpty = !isLoading && merchants.length === 0 && !hasActiveFilters;

  return (
    <Stack gap={2}>
      <Typography variant="body2" color="text.secondary">
        {t(RESOURCES.Description)}
      </Typography>
      {isEmpty ? (
        <Typography variant="body1">{t(RESOURCES.EmptyState)}</Typography>
      ) : (
        <Box display="grid" alignItems="start">
          <UnassignedMerchantsTable
            data={merchants}
            isLoading={isLoading}
            isRefetching={isRefetching}
            onRowClick={setSelectedMerchant}
            rowCount={data?.totalCount ?? 0}
            tableProps={tableProps}
          />
        </Box>
      )}
      <AssignHunterDialog
        merchant={selectedMerchant}
        isOpen={!!selectedMerchant}
        onClose={() => setSelectedMerchant(null)}
      />
    </Stack>
  );
};
